import { START_NODE_ID } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import { STATUS_HEIGHT } from './ellipsis.tsx';
import {
	type ConditionalTransitionIcon,
	type StartNode,
	type StatefulDiagramStatus,
	type StatefulDiagramTransition,
	LayerIndex,
	type DiagramTransition,
	type DiagramStatus,
	type Point,
} from './toPortInfo.tsx';

export function getTransitionLayer(transition: StatefulDiagramTransition) {
	if (transition.isHovered) {
		return LayerIndex.Hover;
	}

	if (transition.isRerouting) {
		return LayerIndex.Proposals;
	}

	if (transition.isSelected || transition.isTargeted || transition.isHighlighted) {
		return LayerIndex.PrioritisedTransitions;
	}

	return LayerIndex.TransitionsDefault;
}

export function getStatusLayer(status: StatefulDiagramStatus) {
	if (status.isHovered) {
		return LayerIndex.Hover;
	}

	if (status.isDragging || status.isMoving) {
		return LayerIndex.Proposals;
	}

	if (status.isSelected || status.isTargeted) {
		return LayerIndex.PrioritisedStatuses;
	}

	return LayerIndex.Default;
}

export function getStartNodeLayer(startNode: StartNode) {
	if (startNode.isHovered) {
		return LayerIndex.Hover;
	}

	if (startNode.isDragging || startNode.isMoving) {
		return LayerIndex.Proposals;
	}

	if (startNode.isTargeted) {
		return LayerIndex.PrioritisedStatuses;
	}

	return LayerIndex.Default;
}

export const applyTransitionIconConditions = (
	icons: ReadonlyArray<ConditionalTransitionIcon>,
	from: string | string[] | undefined,
) => {
	switch (true) {
		case typeof from === 'string':
			return icons
				.filter(({ fromStatuses }) => !Array.isArray(fromStatuses) || fromStatuses.includes(from))
				.map(({ type }) => type);
		case Array.isArray(from):
			return icons
				.filter(
					({ fromStatuses }) =>
						!Array.isArray(fromStatuses) || fromStatuses.some((id) => from.includes(id)),
				)
				.map(({ type }) => type);
		default:
			return icons
				.filter(({ fromStatuses }) => !Array.isArray(fromStatuses))
				.map(({ type }) => type);
	}
};

export const getTransitionSource = (
	transition: DiagramTransition,
): string | string[] | undefined => {
	if (transition.type === 'global-looped') {
		return undefined;
	}

	if (transition.type === 'global') {
		return undefined;
	}

	if (transition.type === 'shared') {
		return transition.links.map((link) => link.source);
	}

	return transition.source;
};

const GLOBAL_LOOPS_CONTAINER_OFFSET_X = -250;
const GLOBAL_LOOPS_CONTAINER_OFFSET_Y = -50;

export const calculateDefaultGlobalLoopsContainerPosition = (
	startNodePosition: Point | undefined,
	statuses: DiagramStatus[],
	defaultPositions: Record<string, Point>,
) => {
	const positionables: (Point | undefined)[] = [
		startNodePosition || defaultPositions[START_NODE_ID],
		...statuses.map(({ id, position }) => position || defaultPositions[id]),
	];

	const areAllPositionablesStable = !positionables.some((position) => !position);

	if (!areAllPositionablesStable) {
		return undefined;
	}

	let minY: number | undefined;
	let maxY: number | undefined;
	let minX: number | undefined;

	positionables.forEach((position) => {
		if (!position) return;

		if (maxY === undefined || position.y + STATUS_HEIGHT > maxY) {
			maxY = position.y + STATUS_HEIGHT;
		}

		if (minY === undefined || position.y < minY) {
			minY = position.y;
		}

		if (minX === undefined || position.x < minX) {
			minX = position.x;
		}
	});

	const x = (minX || 0) + GLOBAL_LOOPS_CONTAINER_OFFSET_X;
	const centerY = ((maxY || 0) + (minY || 0)) / 2 + GLOBAL_LOOPS_CONTAINER_OFFSET_Y;

	return { x, y: centerY };
};
